<script setup lang="ts">
import { computed, ref } from 'vue'
import { ArrowLeft, FileText, Copy, Check, ExternalLink } from 'lucide-vue-next'
import type { BridgeOrganization } from '@/types/bridge'
import { isBridgeUrlSafe } from '@/config/bridge'

const props = defineProps<{
  bridgeOrg: BridgeOrganization | null
  orgId: number
}>()

const emit = defineEmits<{
  (e: 'back'): void
}>()

const tosUrl = computed(() => props.bridgeOrg?.link?.tos_url ?? null)
const kybUrl = computed(() => props.bridgeOrg?.link?.kyc_url ?? null)

const copiedTos = ref(false)
const copiedKyb = ref(false)

const copyToClipboard = async (url: string, which: 'tos' | 'kyb') => {
  await navigator.clipboard.writeText(url)
  if (which === 'tos') {
    copiedTos.value = true
    setTimeout(() => { copiedTos.value = false }, 2000)
  } else {
    copiedKyb.value = true
    setTimeout(() => { copiedKyb.value = false }, 2000)
  }
}
</script>

<template>
  <div class="h-full flex flex-col">
    <!-- Header -->
    <div class="px-4 py-3 border-b flex-shrink-0 flex items-center gap-2">
      <button @click="emit('back')" class="text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
        <ArrowLeft class="h-4 w-4" />
      </button>
      <h2 class="font-semibold text-sm">KYB Onboarding</h2>
    </div>

    <div class="flex-1 overflow-y-auto p-4">
      <div class="max-w-[600px] space-y-3">
      <p class="text-sm text-muted-foreground">
        Share these links with the organization's primary contact to complete onboarding.
      </p>
      <p class="text-sm text-muted-foreground">
        These have been emailed to the organization's admin contact.
      </p>

      <!-- ToS Link -->
      <div v-if="tosUrl" class="rounded-lg border bg-card p-4 space-y-3">
        <div class="flex items-center gap-2">
          <FileText class="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span class="text-sm font-medium">Terms of Service</span>
        </div>
        <p class="text-sm text-muted-foreground">
          The contact must review and accept Bridge's payment terms before KYB can begin.
        </p>
        <div class="flex items-center gap-2">
          <span
            class="flex-1 text-xs font-mono truncate"
            :class="isBridgeUrlSafe(tosUrl) ? 'text-blue-600' : 'text-muted-foreground line-through'"
            :title="!isBridgeUrlSafe(tosUrl) ? 'Production URL — disabled on local/dev' : undefined"
          >{{ tosUrl }}</span>
          <span :title="!isBridgeUrlSafe(tosUrl) ? 'Production URL — disabled on local/dev' : 'Copy link'">
            <button
              @click="copyToClipboard(tosUrl!, 'tos')"
              class="flex-shrink-0 transition-colors"
              :class="isBridgeUrlSafe(tosUrl) ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground/40 cursor-not-allowed'"
              :disabled="!isBridgeUrlSafe(tosUrl)"
            >
              <Check v-if="copiedTos" class="h-4 w-4 text-green-600" />
              <Copy v-else class="h-4 w-4" />
            </button>
          </span>
          <a
            v-if="isBridgeUrlSafe(tosUrl)"
            :href="tosUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            title="Open link"
          >
            <ExternalLink class="h-4 w-4" />
          </a>
          <span v-else class="flex-shrink-0 text-muted-foreground/40 cursor-not-allowed" title="Production URL — disabled on local/dev">
            <ExternalLink class="h-4 w-4" />
          </span>
        </div>
      </div>

      <!-- KYB Link -->
      <div v-if="kybUrl" class="rounded-lg border bg-card p-4 space-y-3">
        <div class="flex items-center gap-2">
          <FileText class="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span class="text-sm font-medium">Business Verification (KYB)</span>
        </div>
        <p class="text-sm text-muted-foreground">
          Identity and business verification via Persona. Must be completed after ToS acceptance.
        </p>
        <div class="flex items-center gap-2">
          <span
            class="flex-1 text-xs font-mono truncate"
            :class="isBridgeUrlSafe(kybUrl) ? 'text-blue-600' : 'text-muted-foreground line-through'"
            :title="!isBridgeUrlSafe(kybUrl) ? 'Production URL — disabled on local/dev' : undefined"
          >{{ kybUrl }}</span>
          <span :title="!isBridgeUrlSafe(kybUrl) ? 'Production URL — disabled on local/dev' : 'Copy link'">
            <button
              @click="copyToClipboard(kybUrl!, 'kyb')"
              class="flex-shrink-0 transition-colors"
              :class="isBridgeUrlSafe(kybUrl) ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground/40 cursor-not-allowed'"
              :disabled="!isBridgeUrlSafe(kybUrl)"
            >
              <Check v-if="copiedKyb" class="h-4 w-4 text-green-600" />
              <Copy v-else class="h-4 w-4" />
            </button>
          </span>
          <a
            v-if="isBridgeUrlSafe(kybUrl)"
            :href="kybUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            title="Open link"
          >
            <ExternalLink class="h-4 w-4" />
          </a>
          <span v-else class="flex-shrink-0 text-muted-foreground/40 cursor-not-allowed" title="Production URL — disabled on local/dev">
            <ExternalLink class="h-4 w-4" />
          </span>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>
